
import { useEffect, useRef, useCallback } from 'react';

interface UseAccessibilityEnhancedOptions {
  announcePageChange?: boolean;
  manageFocus?: boolean;
  trapFocus?: boolean;
  autoFocus?: boolean;
}

export function useAccessibilityEnhanced({
  announcePageChange = true,
  manageFocus = true,
  trapFocus = false,
  autoFocus = false
}: UseAccessibilityEnhancedOptions = {}) {
  const containerRef = useRef<HTMLElement>(null);
  const lastFocusedElement = useRef<HTMLElement | null>(null);

  // Annuncia cambio di pagina per screen reader
  const announceToScreenReader = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', priority);
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    
    // Rimuovi dopo l'annuncio
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }, []);

  // Gestione focus per modali e dialog
  const trapFocusInContainer = useCallback((container: HTMLElement) => {
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement?.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement?.focus();
          e.preventDefault();
        }
      }
    };

    container.addEventListener('keydown', handleTabKey);
    
    return () => {
      container.removeEventListener('keydown', handleTabKey);
    };
  }, []);

  // Auto-focus al primo elemento interattivo
  const focusFirstElement = useCallback(() => {
    if (!containerRef.current) return;
    
    const firstFocusable = containerRef.current.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    ) as HTMLElement;
    
    firstFocusable?.focus();
  }, []);

  // Gestione focus per modali
  const handleModalOpen = useCallback(() => {
    lastFocusedElement.current = document.activeElement as HTMLElement;
    
    if (containerRef.current && trapFocus) {
      return trapFocusInContainer(containerRef.current);
    }
  }, [trapFocus, trapFocusInContainer]);

  const handleModalClose = useCallback(() => {
    lastFocusedElement.current?.focus();
  }, []);

  // Gestione tasti di scelta rapida
  const handleKeyboardShortcuts = useCallback((e: KeyboardEvent) => {
    // ESC per chiudere modali
    if (e.key === 'Escape' && trapFocus) {
      handleModalClose();
    }
    
    // Skip links con Alt+1,2,3
    if (e.altKey) {
      switch (e.key) {
        case '1':
          e.preventDefault();
          (document.querySelector('main') as HTMLElement)?.focus();
          announceToScreenReader('Saltato al contenuto principale');
          break;
        case '2':
          e.preventDefault();
          (document.querySelector('nav') as HTMLElement)?.focus();
          announceToScreenReader('Saltato alla navigazione');
          break;
        case '3':
          e.preventDefault();
          (document.querySelector('[role="search"]') as HTMLElement)?.focus();
          announceToScreenReader('Saltato alla ricerca');
          break;
      }
    }
  }, [trapFocus, handleModalClose, announceToScreenReader]);

  // Setup accessibility features
  useEffect(() => {
    document.addEventListener('keydown', handleKeyboardShortcuts);
    
    return () => {
      document.removeEventListener('keydown', handleKeyboardShortcuts);
    };
  }, [handleKeyboardShortcuts]);

  // Auto-focus su mount
  useEffect(() => {
    if (autoFocus) {
      setTimeout(focusFirstElement, 100);
    }
  }, [autoFocus, focusFirstElement]);

  // Annuncia cambio pagina
  useEffect(() => {
    if (announcePageChange) {
      const pageTitle = document.title;
      announceToScreenReader(`Pagina caricata: ${pageTitle}`);
    }
  }, [announcePageChange, announceToScreenReader]);

  return {
    containerRef,
    announceToScreenReader,
    focusFirstElement,
    handleModalOpen,
    handleModalClose,
    trapFocusInContainer
  };
}
